import { onMounted, ref, type Ref } from 'vue';
import { useVisibility } from '@common/composables';

export interface AppUpdateApi {
	/** Vrai lorsqu'une nouvelle version du paquet web est disponible. */
	updateAvailable: Ref<boolean>;
	/** Version actuellement chargée. */
	currentVersion: Ref<string | null>;
	/** Version publiée en ligne, quand elle diffère de celle chargée. */
	remoteVersion: Ref<string | null>;
	/** Applique la mise à jour et recharge l'application. */
	applyUpdate: () => Promise<void>;
	/** Force une vérification immédiate. */
	check: () => Promise<boolean>;
}

/**
 * Adresse du paquet web publié.
 *
 * Indispensable dans la coque native : celle-ci sert le paquet embarqué depuis
 * `capacitor://localhost`, si bien qu'interroger sa propre origine renverrait
 * toujours la version embarquée — aucune mise en production ne serait jamais
 * détectée. C'est donc le déploiement web qui fait référence.
 */
const WEB_BASE_URL = (import.meta.env.VITE_WEB_BASE_URL ?? '').replace(/\/$/, '');

const APPLIED_VERSION_KEY = 'taxi_web_version';

/**
 * Mise à jour du contenu web à l'ouverture.
 *
 * L'application est distribuée sous trois formes qui partagent le même paquet
 * web : site, PWA installée, et coque native Android/iOS. Une correction d'écran
 * doit pouvoir être livrée sans republication sur les magasins — seules les
 * évolutions natives (permissions, plugins) l'exigent réellement.
 *
 * Deux mécanismes, selon le contexte :
 *
 *  - **web et PWA** — le service worker de Workbox récupère le nouveau paquet ;
 *    on l'interroge à l'ouverture et à chaque reprise.
 *
 *  - **coque native** — le paquet embarqué est comparé au `version.json` servi
 *    par le déploiement web. S'il est plus ancien, l'application bascule la vue
 *    sur le paquet en ligne : le contenu est alors à jour sans passer par les
 *    magasins, et la version embarquée reste le repli hors connexion.
 *
 * La mise à jour n'est **jamais** appliquée d'office en pleine course :
 * recharger ferait perdre au client son écran de suivi, et au chauffeur son
 * écran de course. Ce composable signale, l'appelant choisit le moment.
 */
export function useAppUpdate(options: { autoCheck?: boolean } = {}): AppUpdateApi {
	const updateAvailable = ref(false);
	const currentVersion = ref<string | null>(null);
	const remoteVersion = ref<string | null>(null);

	let serviceWorkerUpdate: ((reload?: boolean) => Promise<void>) | null = null;
	let isNative = false;

	/** Lit un marqueur de version, en contournant tout cache intermédiaire. */
	const readVersion = async (baseUrl: string): Promise<string | null> => {
		try {
			const response = await fetch(`${baseUrl}/version.json?t=${Date.now()}`, {
				cache: 'no-store',
			});
			if (!response.ok) {
				return null;
			}
			const payload = (await response.json()) as { version?: string };
			return payload.version ?? null;
		} catch {
			// Hors connexion : on conserve la version en place, sans rien signaler.
			return null;
		}
	};

	const check = async (): Promise<boolean> => {
		const local = await readVersion('');
		if (local) {
			currentVersion.value = local;
		}

		// Coque native : la référence est le déploiement web, pas l'origine locale.
		if (isNative && WEB_BASE_URL) {
			const published = await readVersion(WEB_BASE_URL);
			remoteVersion.value = published;

			if (published && local && published !== local) {
				updateAvailable.value = true;
			}
			return updateAvailable.value;
		}

		// Web et PWA : le service worker signale lui-même un paquet en attente
		// (`onNeedRefresh`). La comparaison de marqueurs sert de filet quand il est
		// indisponible — origine non sécurisée, WebView ancienne.
		if (!local) {
			return updateAvailable.value;
		}

		const applied = window.localStorage.getItem(APPLIED_VERSION_KEY);
		if (!applied) {
			window.localStorage.setItem(APPLIED_VERSION_KEY, local);
			return updateAvailable.value;
		}
		if (applied !== local) {
			updateAvailable.value = true;
		}
		return updateAvailable.value;
	};

	const applyUpdate = async (): Promise<void> => {
		// Coque native : on bascule la vue sur le paquet publié. Le contenu
		// embarqué reste en place et sert de repli au prochain démarrage hors
		// connexion.
		if (isNative && WEB_BASE_URL && remoteVersion.value) {
			window.location.replace(`${WEB_BASE_URL}/?v=${encodeURIComponent(remoteVersion.value)}`);
			return;
		}

		// La version visée est notée **avant** le rechargement : sans cela, la
		// page revient, se compare de nouveau à elle-même et se recharge en
		// boucle.
		if (currentVersion.value) {
			window.localStorage.setItem(APPLIED_VERSION_KEY, currentVersion.value);
		}

		if (serviceWorkerUpdate) {
			// Active un éventuel service worker en attente. Attention : quand la
			// mise à jour a été détectée par comparaison de marqueurs et non par le
			// service worker lui-même, il n'y en a aucun en attente — l'appel rend
			// alors la main sans rien faire. C'était tout le problème : le bouton
			// semblait sans effet.
			await serviceWorkerUpdate(true).catch(() => undefined);
		}

		// Rechargement dans tous les cas, en contournant le cache : c'est lui qui
		// applique réellement la nouvelle version.
		window.location.replace(`${window.location.pathname}${window.location.search}`);
	};

	// Reprise après veille : l'application chauffeur et la borne restent ouvertes
	// des heures, c'est le moment naturel de revérifier une mise en production.
	useVisibility({ onVisible: () => void check() });

	onMounted(async () => {
		try {
			const { Capacitor } = await import('@capacitor/core');
			isNative = Capacitor.isNativePlatform();
		} catch {
			// Hors coque native : le module n'est pas présent.
		}

		// Import dynamique : le module virtuel n'existe qu'avec le greffon PWA et
		// ne doit pas casser un rendu de test ou de développement sans lui.
		try {
			const { registerSW } = await import('virtual:pwa-register');
			serviceWorkerUpdate = registerSW({
				immediate: true,
				onNeedRefresh() {
					updateAvailable.value = true;
				},
			});
		} catch {
			// Service worker indisponible.
		}

		if (options.autoCheck !== false) {
			await check();
		}
	});

	return { updateAvailable, currentVersion, remoteVersion, applyUpdate, check };
}
